<template>
  <div>
    <!-- Help Modal - Positioned in main content area, not covering sidebar -->
    <teleport to="body">
      <div v-if="showHelpModal" class="help-modal-container">
        <!-- Overlay only covers main content area (excludes sidebar) -->
        <div 
          class="help-modal-overlay" 
          @click="closeHelpModal"
        ></div>
        <!-- Modal content positioned in center of main content area -->
        <div 
          class="help-modal-content"
        >
          <div class="flex items-center justify-between p-4 border-b">
            <h3 class="text-2xl font-normal text-blue-600">{{ helpModalTitle }}</h3>
            <button @click="closeHelpModal" class="text-gray-400 hover:text-gray-600">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>
          <div class="py-6 overflow-y-auto max-h-[calc(90vh-120px)] help-content-wrapper">
            <component :is="resolvedHelpComponent" v-if="resolvedHelpComponent" />
            <!-- <iframe :src="helpModalUrl" class="w-full h-[calc(90vh-200px)] border-0" frameborder="0" loading="lazy">
            </iframe> -->
          </div>
        </div>
      </div>
    </teleport>

    <!-- OnlyFans Connect Modal -->
    <div v-if="showOnlyFansModal" class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="fixed inset-0 bg-black opacity-50" @click="closeOnlyFansModal"></div>
      <div class="relative bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
        <div class="flex items-center justify-between p-4 border-b">
          <h3 class="text-xl font-semibold text-gray-900">Connect OnlyFans Account</h3>
          <button @click="closeOnlyFansModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div class="p-6">
          <form @submit.prevent="connectOnlyFans">
            <div class="mb-4">
              <label for="email" class="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input type="email" id="email" v-model="onlyFansForm.email" required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your OnlyFans email" />
            </div>
            <div class="mb-6">
              <label for="password" class="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <input type="password" id="password" v-model="onlyFansForm.password" required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter your OnlyFans password" />
            </div>
            <div class="flex justify-end space-x-3">
              <button type="button" @click="closeOnlyFansModal"
                class="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500">
                Cancel
              </button>
              <button type="submit" :disabled="isConnecting"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed">
                {{ isConnecting ? 'Connecting...' : 'Connect Account' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Top Bar for Small/Medium Screens -->
    <div class="fixed top-0 left-0 w-full flex items-center justify-between bg-white p-4 shadow z-40 lg:hidden">
      <router-link to="/" class="flex items-center">
        <img src="../assets/optimaAI.png" alt="OptimaAI Logo" class="h-9 w-9 cursor-pointer" />
        <!-- <div class="ml-3 flex flex-col">
          <span class="text-lg font-semibold text-gray-900">OptimaAI Creator</span>
          <span v-if="userName || profileData?._doc.firstName" class="text-xs font-medium text-green-600">
            Hello {{ userName || profileData?._doc.firstName }}
          </span>
        </div> -->
      </router-link>

      <button @click="showMobileSidebar = true">
        <!-- Burger Icon -->
        <svg class="h-8 w-8 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
    </div>

    <!-- Sidebar (Desktop) -->
    <div class="relative h-screen hidden lg:flex sidebar-desktop" style="z-index: 60; position: relative;">
      <div class="h-full bg-white border-r flex flex-col w-90">
        <!-- Logo Section -->
        <router-link to="/welcome" class="p-5 flex items-start cursor-pointer justify-start">
          <img src="../assets/optimaAI.png" alt="OptimaAI Logo" class="h-8 w-8 flex-shrink-0 mt-0.5" />
          <div class="ml-3 flex flex-col">
            <span class="text-xl font-semibold text-gray-900 transition-opacity duration-300 opacity-100">OptimaAI
              Creator</span>
            <!-- Personalized Greeting -->
            <div v-if="userName || profileData?._doc.firstName"
              class="custom-greeting font-semibold  text-green-600 cursor-pointer hover:text-green-700 transition-colors"
              @click="goToWelcome">
              Hello {{ capitalizedName }}
            </div>
          </div>
        </router-link>

        <!-- Navigation Links -->
        <nav class="mt-8 flex-1 overflow-y-auto custom-scrollbar">
          <!-- Dashboard -->
          <div :class="[$route.path === '/dashboard' ? 'bg-blue-50' : '']">
            <router-link to="/dashboard" @click="showMobileSidebar = false"
              class="flex items-center justify-between px-5 py-2 transition-all duration-300 relative" :class="[
                $route.path === '/dashboard'
                  ? 'text-blue-600'
                  : 'text-gray-600',
              ]">
              <!-- Left block: icon + label + subtitle -->
              <div class="flex flex-col justify-center">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 flex-shrink-0" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
                  </svg>
                  <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                    Dashboard
                  </span>
                </div>

                <!-- Subtitle directly below title -->
                <div class="ml-10 -mt-1">
                  <a href="" class="text-[10px] text-gray-600 ">
                    Manage your Dashboard
                  </a>
                </div>
              </div>

              <!-- Right big gray play icon -->
              <!-- <button @click="openHelpModal('/help/dashboard', 'Dashboard Help')"
                class="ml-4 cursor-pointer block">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                  fill="currentColor">
                  <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                  <polygon points="10,8 16,12 10,16" fill="currentColor" />
                </svg>
              </button> -->


            </router-link>
          </div>



          <!-- Conversations -->
          <div :class="[
            $route.path.startsWith('/conversations') ? 'bg-blue-50' : '',
          ]">
            <router-link to="/conversations" @click="showMobileSidebar = false"
              class="flex items-center justify-between px-5 py-2 transition-all duration-300 relative" :class="[
                $route.path.startsWith('/conversations')
                  ? 'text-blue-600'
                  : 'text-gray-600',
              ]">
              <!-- Left: icon + label + subtitle -->
              <div class="flex flex-col justify-center">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 flex-shrink-0" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                    AI-Assisted Messaging
                  </span>
                </div>

                <!-- Subtitle below title -->
                <div class="flex ">
                  <a href="" class="text-[10px] text-gray-600 mr-2">
                    Module 1
                  </a>
                  <a href="" class="text-[10px] text-gray-600 ">
                    Automate chats smartly
                  </a>
                  <!-- <a href="" class="text-[10px] text-gray-600 ">
                    Module 1
                  </a> -->
                </div>
              </div>

              <!-- Right big gray play icon -->
              <div class="ml-4 flex flex-col items-center">
                <button @click="openHelpModal('/help/ai-messaging', 'Information and Video')"
                  class="cursor-pointer block">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                    fill="currentColor">
                    <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                    <polygon points="10,8 16,12 10,16" fill="currentColor" />
                  </svg>
                </button>
                <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
              </div>

            </router-link>
          </div>

          <!-- Repeat this pattern for all other sidebar items, ensuring the background and secondary link are inside the same parent div. -->
          <div :class="[$route.path === '/calendar' || $route.path === '/analytics' || $route.path === '/content-library' ? 'bg-blue-50' : '']">
            <router-link to="/calendar" @click="showMobileSidebar = false"
              class="flex items-center justify-between px-5 py-2 transition-colors" :class="[
                $route.path === '/calendar' || $route.path === '/analytics' || $route.path === '/content-library'
                  ? 'text-blue-600'
                  : 'text-gray-600 hover:bg-gray-50',
              ]">
              <!-- Left Icon + Title + Subtitle -->
              <div class="flex flex-col justify-center">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                    Content Calendar
                  </span>
                </div>

                <!-- Subtitle -->
                <div class="flex ">
                  <a href="" class="text-[10px] text-gray-600 mr-2">
                    Module 2
                  </a>
                  <a href="" class="text-[10px] text-gray-600 ">
                    Plan posts effectively
                  </a>
                  <!-- <a href="" class="text-[10px] text-gray-600 ">
                    Module 2
                  </a> -->
                </div>
              </div>

              <!-- Right: Large Video Icon -->
              <div class="ml-4 flex flex-col items-center">
                <button @click="openHelpModal('/help/content-calendar', 'Information and Video')"
                  class="cursor-pointer block">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                    fill="currentColor">
                    <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                    <polygon points="10,8 16,12 10,16" fill="currentColor" />
                  </svg>
                </button>
                <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
              </div>

            </router-link>
          </div>

          <div :class="[$route.path === '/monetization-hub' ? 'bg-blue-50' : '']">
            <router-link to="/monetization-hub" @click="showMobileSidebar = false"
              class="flex items-center justify-between px-5 py-2 transition-colors relative" :class="[
                $route.path === '/monetization-hub'
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50',
              ]">
              <!-- Left: Icon and Text Block -->
              <div class="flex flex-col justify-center">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 10c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                  </svg>
                  <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                    Subscription & PPV
                  </span>
                </div>

                <!-- Subtitle -->
                <div class="flex ">
                  <a href="" class="text-[10px] text-gray-600 mr-2">
                    Module 3
                  </a>
                  <a href="" class="text-[10px] text-gray-600 ">
                    Monetize your content
                  </a>
                  <!-- <a href="" class="text-[10px] text-gray-600 ">
                    Module 3
                  </a> -->
                </div>
              </div>

              <!-- Right: Large Video Icon -->
              <div class="ml-4 flex flex-col items-center">
                <button @click="openHelpModal('/help/Subscription-ppv', 'Information and Video')"
                  class="cursor-pointer block">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                    fill="currentColor">
                    <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                    <polygon points="10,8 16,12 10,16" fill="currentColor" />
                  </svg>
                </button>
                <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
              </div>
            </router-link>
          </div>
          <!-- Analytics Dashboard -->
          <div :class="[$route.path === '/analytics-dashboard' ? 'bg-blue-50' : '']">
            <router-link to="/analytics-dashboard" @click="showMobileSidebar = false"
              class="flex items-center justify-between px-5 py-2 transition-all duration-300 relative" :class="[
                $route.path === '/analytics-dashboard'
                  ? 'text-blue-600'
                  : 'text-gray-600',
              ]">
              <!-- Left block: icon + label + subtitle -->
              <div class="flex flex-col justify-center">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 flex-shrink-0" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                  <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                    Analytics Dashboard
                  </span>
                </div>

                <!-- Subtitle directly below title -->
                <div class="flex ">
                  <a href="" class="text-[10px] text-gray-600 mr-2">
                    Module 4
                  </a>
                  <a href="" class="text-[10px] text-gray-600 ">
                    Visual performance metrics
                  </a>

                </div>
              </div>

              <!-- Right big gray play icon -->
              <div class="ml-4 flex flex-col items-center">
                <button @click="openHelpModal('/help/analytics-dashboard', 'Information and Video')"
                  class="cursor-pointer block">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                    fill="currentColor">
                    <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                    <polygon points="10,8 16,12 10,16" fill="currentColor" />
                  </svg>
                </button>
                <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
              </div>


            </router-link>
          </div>
          <div :class="[
            $route.path === '/revenue-optimization' ? 'bg-blue-50' : '',
          ]">
            <router-link to="/revenue-optimization" @click="showMobileSidebar = false"
              class="flex items-center justify-between px-5 py-2 transition-colors" :class="[
                $route.path === '/revenue-optimization'
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50',
              ]">
              <!-- Left Content -->
              <div class="flex flex-col justify-center">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                    Advanced Revenue
                  </span>
                </div>

                <!-- Subtitle under text -->
                <div class="flex ">
                  <a href="" class="text-[10px] text-gray-600 mr-2">
                    Module 5
                  </a>
                  <a href="" class="text-[10px] text-gray-600 ">
                    Optimize earnings smartly
                  </a>
                </div>
              </div>

              <!-- Right Icon -->
              <div class="ml-4 flex flex-col items-center">
                <button @click="openHelpModal('/help/advance-revenue-optimization', 'Information and Video')"
                  class="cursor-pointer block">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                    fill="currentColor">
                    <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                    <polygon points="10,8 16,12 10,16" fill="currentColor" />
                  </svg>
                </button>
                <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
              </div>
            </router-link>
          </div>

          <div :class="[
            $route.path === '/smart-content-tagging' ? 'bg-blue-50' : '',
          ]">
            <router-link to="/smart-content-tagging" @click="showMobileSidebar = false"
              class="flex items-center justify-between px-5 py-2 transition-colors" :class="[
                $route.path === '/smart-content-tagging'
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50',
              ]">
              <!-- Left: Icon and Title -->
              <div class="flex flex-col justify-center">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                    Smart Content Tagging
                  </span>
                </div>

                <div class="flex ">
                  <a href="" class="text-[10px] text-gray-600 mr-2">
                    Module 6
                  </a>
                  <a href="" class="text-[10px] text-gray-600 ">
                    Tag content with AI

                  </a>
                </div>
              </div>

              <!-- Right video icon -->
              <div class="ml-4 flex flex-col items-center">
                <button @click="openHelpModal('/help/smart-content', 'Information and Video')"
                  class="cursor-pointer block">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                    fill="currentColor">
                    <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                    <polygon points="10,8 16,12 10,16" fill="currentColor" />
                  </svg>
                </button>
                <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
              </div>
            </router-link>
          </div>

          <div :class="[$route.path === '/security-privacy' ? 'bg-blue-50' : '']">
            <router-link to="/security-privacy" @click="showMobileSidebar = false"
              class="flex items-center justify-between px-5 py-2 transition-colors" :class="[
                $route.path === '/security-privacy'
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50',
              ]">
              <!-- Left Content -->
              <div class="flex flex-col justify-center">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-600" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 22s8-4 8-10V5a2 2 0 00-2-2H6a2 2 0 00-2 2v7c0 6 8 10 8 10z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                  <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                    Security & Privacy
                  </span>
                </div>

                <div class="flex ">
                  <a href="" class="text-[10px] text-gray-600 mr-2">
                    Module 7
                  </a>
                  <a href=" " class="text-[11px] text-gray-600 ">
                    Data safety tools
                  </a>
                </div>
              </div>

              <!-- Right video icon -->
              <div class="ml-4 flex flex-col items-center">
                <button @click="openHelpModal('/help/security-privacy', 'Information and Video')"
                  class="cursor-pointer block">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                    fill="currentColor">
                    <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                    <polygon points="10,8 16,12 10,16" fill="currentColor" />
                  </svg>
                </button>
                <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
              </div>
            </router-link>
          </div>
 
          <div :class="[$route.path === '/Onboarding&Migration' ? 'bg-blue-50' : '']">
            <router-link to="/Onboarding&Migration" @click="showMobileSidebar = false"
              class="flex items-center justify-between px-5 py-2 transition-colors" :class="[
                $route.path === '/Onboarding&Migration'
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50',
              ]">
              <div class="flex flex-col justify-center">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 " fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                    Onboarding & Migration
                  </span>
                </div>
                <div class="flex ">
                  <a href="" class="text-[10px] text-gray-600 mr-2">
                    Module 8
                  </a>
                  <a href=" " class="text-[11px] text-gray-600 ">
                    Trusted Migration Tools
                  </a>
                </div>
              </div>
              <div class="ml-4 flex flex-col items-center">
                <button @click="openHelpModal('/help/Onboarding-Migration', 'Information and Video')"
                  class="cursor-pointer block">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                    fill="currentColor">
                    <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                    <polygon points="10,8 16,12 10,16" fill="currentColor" />
                  </svg>
                </button>
                <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
              </div>
            </router-link>
          </div>
           <div :class="[$route.path.startsWith('/connect-accounts') ? 'bg-blue-50' : '']">
            <router-link to="/connect-accounts/account" @click="showMobileSidebar = false"
              class="flex items-center justify-between px-5 py-2 transition-colors relative" :class="[
                $route.path.startsWith('/connect-accounts')
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50',
              ]">
              <div class="flex flex-col justify-center">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
                  <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">Connect Accounts</span>
                </div>
                <div class="flex ">
                  <a href="" class="text-[10px] text-gray-600 mr-2">
                    Module 9
                  </a>
                  <a href=" " class="text-[11px] text-gray-600 ">
                    Links your social account
                  </a>
                </div>
              </div>
              <div class="ml-4 flex flex-col items-center">
                <button @click="openHelpModal('/help/connect-accounts', 'Information and Video')"
                  class="cursor-pointer block">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                    fill="currentColor">
                    <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                    <polygon points="10,8 16,12 10,16" fill="currentColor" />
                  </svg>
                </button>
                <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
              </div>
            </router-link>
          </div>
          <!-- Fan Database (icon, text, and subtitle for alignment) -->
          <div :class="[$route.path === '/fan-insights' ? 'bg-blue-50' : '']">
            <router-link to="/fan-insights" @click="showMobileSidebar = false"
              class="flex items-center justify-between px-5 py-2 transition-colors relative" :class="[
                $route.path === '/fan-insights'
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50',
              ]">
              <div class="flex flex-col justify-center">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">Fan Database</span>
                </div>
                <div class="flex ">
                  <a href="" class="text-[10px] text-gray-600 mr-2">
                    Module 10
                  </a>
                  <a href=" " class="text-[11px] text-gray-600 ">
                    View and manage your fans
                  </a>
                </div>
              </div>
              <div class="ml-4 flex flex-col items-center">
                <button @click="openHelpModal('/help/fan-database', 'Information and Video')"
                  class="cursor-pointer block">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                    fill="currentColor">
                    <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                    <polygon points="10,8 16,12 10,16" fill="currentColor" />
                  </svg>
                </button>
                <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
              </div>
            </router-link>
          </div>

          <!-- Mobile Experience -->
          <div>
            <button @click="openHelpModal('/help/mobile-experience', 'Information and Video')"
              class="flex items-center justify-between w-full px-5 py-2 transition-colors relative text-gray-600 hover:bg-gray-50">
              <div class="flex flex-col justify-center">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">Mobile Experience</span>
                </div>

                <!-- Subtitle directly below title -->
                <div class="ml-10 -mt-1">
                  <a href="" class="text-[10px] text-gray-600 ">
                    Optimize for mobile devices
                  </a>
                </div>
              </div>
              <div class="ml-4 flex flex-col items-center">
                <button @click.stop="openHelpModal('/help/mobile-experience', 'Information and Video')"
                  class="cursor-pointer block">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                    fill="currentColor">
                    <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                    <polygon points="10,8 16,12 10,16" fill="currentColor" />
                  </svg>
                </button>
                <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
              </div>
            </button>
          </div>

          <!-- OnlyFans Connect Button -->
          <!-- <div class="mt-4 border-t border-gray-200 pt-4">
            <button @click="openOnlyFansModal"
              class="flex items-center w-full px-5 py-3 transition-colors text-gray-600 hover:bg-blue-50 hover:text-blue-600">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                Connect OnlyFans
              </span>
            </button>
          </div> -->

          <!-- Admin Section (Only for Admins) -->
          <div v-if="isAdmin" class="mt-2 text-blue-600">
            <button @click="isAdminOpen = !isAdminOpen"
              class="flex items-center justify-between w-full px-5 py-3 text-gray-600 hover:bg-gray-50 transition-colors">
              <div class="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0 font-medium">Admin</span>
              </div>
              <svg :class="{'rotate-180': isAdminOpen}" class="h-4 w-4 transform transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <div v-show="isAdminOpen" class="bg-gray-800">
              <router-link to="/admin/support" 
                class="flex items-center pl-14 py-2 text-sm transition-colors text-gray-300 hover:text-white hover:bg-gray-700">
                Support
              </router-link>
            </div>
          </div>
          <div class="mt-2 border-t border-gray-200 pt-4">
            <button @click="handleLogout"
              class="flex items-center w-full px-5 py-3 transition-colors text-gray-600 hover:bg-red-50 hover:text-red-600">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                Logout
              </span>
            </button>
          </div>
          <!-- <router-link to="/authentic-voice-ai" @click="showMobileSidebar = false"
            class="flex items-center px-5 py-3 transition-colors" :class="[
              $route.path === '/authentic-voice-ai'
                ? 'text-blue-600 bg-blue-50'
                : 'text-gray-600 hover:bg-gray-50',
            ]">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 1a3 3 0 00-3 3v7a3 3 0 006 0V4a3 3 0 00-3-3zm6 10a6 6 0 01-12 0m12 0v2a6 6 0 01-6 6m0 0v3m0-3a6 6 0 01-6-6v-2" />
            </svg>
            <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">Authentic Voice AI</span>
          </router-link> -->
          <!-- Cross-Platform Control Center -->
          <!-- <div :class="[
            $route.path === '/cross-platform-control' ? 'bg-blue-50' : '',
          ]"> -->
          <!-- <router-link to="/cross-platform-control" @click="showMobileSidebar = false"
            class="flex items-center justify-between px-5 py-2 transition-all duration-300 relative" :class="[
              $route.path === '/cross-platform-control'
                ? 'text-blue-600'
                : 'text-gray-600',
            ]">
            <div class="flex flex-col justify-center">
              <div class="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 flex-shrink-0" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 0a2 2 0 012 2v6a2 2 0 01-2 2m-6 0a2 2 0 002-2v-6a2 2 0 00-2-2" />
                  </svg>
                  <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap" :class="[
                    isExpanded || isMobile
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 -translate-x-4',
                  ]">
                    Cross-Platform Control
                  </span>
                </div> -->

            <!-- Subtitle below title -->
            <!-- <div v-if="isExpanded || isMobile" class="ml-10 -mt-1">
                  <a href="" class="text-[10px] text-gray-600">
                    Manage all platforms
                  </a>
                </div> -->
            <!-- </div> -->

            <!-- Right big gray play icon -->
            <!-- <button @click="openHelpModal('/help/cross-platform-control', 'Cross-Platform Control Center Help')"
                class="ml-4 cursor-pointer block">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                  fill="currentColor">
                  <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                  <polygon points="10,8 16,12 10,16" fill="currentColor" />
                </svg>
              </button> -->

            <!-- </router-link> -->
            <!-- </div> -->

            <!-- Collaborative Content Tools -->
            <!-- <div :class="[
              $route.path === '/collaborative-content-tools' ? 'bg-blue-50' : '',
            ]"> -->
            <!-- <router-link to="/collaborative-content-tools" @click="showMobileSidebar = false"
                class="flex items-center justify-between px-5 py-2 transition-all duration-300 relative" :class="[
                  $route.path === '/collaborative-content-tools'
                    ? 'text-blue-600'
                    : 'text-gray-600',
                ]">
                <div class="flex flex-col justify-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 flex-shrink-0" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap" :class="[
                      isExpanded || isMobile
                        ? 'opacity-100 translate-x-0'
                        : 'opacity-0 -translate-x-4',
                    ]">
                      Collaborative Content Tools
                    </span>
                  </div> -->

            <!-- Subtitle below title -->
            <!-- <div v-if="isExpanded || isMobile" class="ml-10 -mt-1">
                    <a href="" class="text-[10px] text-gray-600">
                      Team content workflows
                    </a>
                  </div> -->
            <!-- </div> -->

            <!-- Right big gray play icon -->
            <!-- <button @click="openHelpModal('/help/collaborative-content', 'Collaborative Content Tools Help')"
                  class="ml-4 cursor-pointer block">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                    fill="currentColor">
                    <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                    <polygon points="10,8 16,12 10,16" fill="currentColor" />
                  </svg>
                </button> -->

            <!-- </router-link> -->
            <!-- </div> -->
        </nav>

        <!-- User Profile Section -->
        <div class="p-5 flex justify-center">
          <div class="flex flex-col w-full">
            <div class="mb-4 ">
              <GlobalChat />
            </div>

            <!-- Subscription Button -->
            <!-- <button @click="goToSubscription"
              class="flex w-full border rounded-xl bg-white shadow-sm transition-colors font-medium group items-center justify-center mb-4"
              :class="[
                $route.path === '/subscription'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-gray-200 text-[#222]',
                'focus:border-blue-500 focus:text-blue-600 hover:border-blue-500 hover:text-blue-600 active:border-blue-600 active:text-blue-700',
              ]">
              <div class="flex flex-col w-full">
                <div class="flex items-center justify-between w-full px-2 py-1">
                  <div class="flex items-center gap-2">
                    <span class="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-blue-500" fill="none"
                        viewBox="0 0 24 24" stroke="currentColor">
                        <circle cx="12" cy="12" r="10" fill="#e0edff" />
                        <path stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                          d="M12 16V8m0 0l-4 4m4-4l4 4" />
                      </svg>
                    </span>
                    <span :class="[
                      'text-base font-medium',
                      $route.path === '/subscription'
                        ? 'text-blue-600'
                        : 'text-[#222] group-hover:text-blue-600 group-focus:text-blue-600 group-active:text-blue-700',
                    ]">
                      {{
                      profileData?.subscription?.planId?.name === "Elite"
                      ? "Elite"
                      : "Upgrade Plan"
                      }}
                    </span>
                  </div>
                  <span class="text-sm text-gray-500">{{
                    profileData?.subscription?.planId?.name || "No Plan"
                    }}</span>
                </div>
                <p class="text-xs text-gray-500 px-2 pb-2 text-center">
                  Upgrade to unlock AI tools and insights.
                </p>
              </div>
            </button> -->

            <!-- Profile Section -->
            <!-- <div class="flex items-center justify-between w-full">
              <div class="flex items-center cursor-pointer" @click="goToProfile">
                <img :src="profileData?._doc.profileImgUrl &&
                  profileData._doc.profileImgUrl !== ''
                  ? `${apiUrl}${profileData._doc.profileImgUrl}`
                  : 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAOEAAADhCAMAAAAJbSJIAAAAV1BMVEX6+vqPj4////+Li4u5ubn8/PyIiIiFhYWJiYnk5OShoaGnp6fT09Pn5+eRkZHu7u7Z2dn19fXCwsKamprHx8exsbHOzs7X19eurq6/v7+jo6Pe3t6WlpZaNtXmAAAE3UlEQVR4nO2d25aqOhBFsUIRbgqI4AX//zsP0fa0vUfbBoKm4ljzpfvROapIIGSFKAIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIEWamG+P/vn/Owhi5Juu3XZHnp6Lblutm1PT9q5aDKRriVulEqZVBqUSr9pjxh0gyrWOlr273KL05Vh/gyDTkv+jdJIsscEemrNUP9K7oU0W+f6UD1Bz+9rs4xuEOrFSrR/15T7rJwiwjU/y8gF9l3IWoyHxKLAVHxS68AYej1qZDbyRFaIocbaYIjhNHHlajTqygIS2CUqRiquDYqHFAinS0H2S+0WUwijzYThP/KFahjDY8vUWvtIEUkeK5hkkYMz9X83rUoJsQ+pTy2YIrFcJ4ytn8EoZRRCocBEMoostVeFH0LfAUOs4dSK8kpfQ2pbOT4Gp1Et6mvHZr0vEOXPhYQ7vU0TCphRueHAXFj6bsKij95pSrOY9N/xQxktymPLgbJqKfobh3HWhGw0GyIW3d5vuLoeg5f/6j4TdpL9qwczdUoh+DYWhDuhPdpY5PFhdD2dfhboGxdC/ZkMsFZvxMtOH64+9pGnfDjWTBBR7xxT/ku08XqejpcGzTvWub6rXsLnW/EIVfhu7LGNIXMdxnRC16NjRw5FZD2as0F9xuTWU//l7hxmVNeCO/hKaI89dqdAAljBxe4wdxFRp4P7dPpc/2/zNnv5AhFT8X3uBonuE5FMG57/IT4e/VfkDldEU9hFPCyCx+T1XU+6AEzaw4TVH3gQmaZbcpisFV0DDlWkzD3K1Pa8ud0EnbBClotut3NmXUx9B2sd9B2fmZo86DjgVFTOXmr4d+fa4DLuAV4rJ9EF5TOg/fz2ACiBud/rRUiT5vPyF+eIWJ1v3hnGidGMY/566sPione00CR1U21HU9rCs2YWffP+kV8A3fPwQAAIAP7k/1WApJkwpTM/THeFmOfRYJuelhGgo13nYuTaJX3VqCI1W5awDhIUof/K+hzlkZneKY+F7Bmb4uOhXPq3DUv1rQ85t916CaHcrjtegSF51gePDWp1y/o4Q+X5y+p4RjETtPRVxiq6UlnmrovkvPFl9tusS2dTt87SNaInpgh68IBh3eJLhSWxjCcK7h265DX4afP9IsEDa0w1cUaomQkx2+olBLhJwsDT09IrqfEGFt6CkKxY17cNsOb3ujqX2Tobfj+N41mCbeUqVzT56bis+T6t4i6HN/+3va1Gde7z3zhdfd0e4H7jzHb5rN7fg5OzwfUjc3WmGPOvp9NeOW47Iy9P16jXavvf3W/o/+ovyVfeptufsO19Do34IiwmxLnO/1EP8vuQ30sttTJeWIjFcpihE0W/Jf0KhqI0fQbDmZeIz+c9JWxjV4g7lYtlN1LGGz0A+of/jBnOkoJTGMSM1iZdSdzNMhmYbzEiOObkVsZ/sVpv7PDJCdn+wcDfH+UQbIhiByQkzZQc8qpEqSWG5/3sMUlYVOJn5nRieHOpxPzfEoWXcbW0uT8oqHcPS+GH9wVXZ33wT81c18JzCP96F+DfGS5lrvt4d8oy65tTS9bJZOr/k1dc67XV1Foae8Lrv4uamqoS77frfd7nZ9X9ZZ1TQsbEe+E1+Zte+gARJsAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACJP/AAFSQ7wNy+LTAAAAAElFTkSuQmCC'
                  " class="h-10 w-10 rounded-full object-cover" alt="User Profile" />
                <div class="ml-4">
                  <p class="text-sm font-medium text-gray-900">
                    {{ profileData?._doc.firstName || "" }}
                  </p>
                  <p class="text-xs text-gray-500">Creator</p>
                </div>
              </div>

              <button @click="handleLogout"
                class="flex items-center text-gray-500 hover:text-red-500 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div> -->
            <!-- <GlobalChat/> -->

          </div>
        </div>
      </div>
    </div>

    <!-- Sidebar Overlay (Mobile/Tablet) -->
    <transition name="fade">
      <div v-if="showMobileSidebar" class="fixed inset-0 z-50 flex">
        <div class="fixed inset-0 bg-black opacity-50" @click="showMobileSidebar = false"></div>
        <div class="relative bg-white w-70 h-full shadow-lg z-50  flex flex-col">
          <!-- Sidebar content (reuse your sidebar code here) -->
          <!-- You can use a component or slot to avoid code duplication -->
          <div class="p-5 flex items-center justify-between">
            <div class="flex items-start">
              <img src="../assets/optimaAI.png" alt="OptimaAI Logo" class="h-8 w-8 flex-shrink-0 mt-0.5" />
              <div class="ml-3 flex flex-col">
                <span class="text-xl font-semibold text-gray-900">OptimaAI Creator</span>
                <!-- Personalized Greeting for Mobile Sidebar -->
                <span v-if="userName || profileData?._doc.firstName"
                  class="custom-greeting text-sm font-medium text-green-600 cursor-pointer hover:text-green-700 transition-colors"
                  @click="goToWelcome">
                  Hello {{ capitalizedName }}
                </span>
              </div>
            </div>
            <button @click="showMobileSidebar = false">
              <!-- Close Icon -->
              <svg class="h-6 w-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <!-- Navigation Links (same as desktop) -->
          <nav class="mt-8 flex-1 overflow-y-auto custom-scrollbar">
            <!-- Dashboard -->
            <div :class="[$route.path === '/dashboard' ? 'bg-blue-50' : '']">
              <router-link to="/dashboard" @click="showMobileSidebar = false"
                class="flex items-center justify-between px-5 py-2 transition-all duration-300 relative" :class="[
                  $route.path === '/dashboard'
                    ? 'text-blue-600'
                    : 'text-gray-600',
                ]">
                <!-- Left block: icon + label + subtitle -->
                <div class="flex flex-col justify-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 flex-shrink-0" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
                    </svg>
                    <span
                      class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                      Dashboard
                    </span>
                  </div>
                  <div class="ml-10 -mt-1">
                    <a href="" class="text-[10px] text-gray-600 ">
                      Manage your Dashboard
                    </a>
                  </div>
                </div>
              </router-link>
            </div>



            <div :class="[$route.path === '/conversations' ? 'bg-blue-50' : '']">
              <router-link to="/conversations" @click="showMobileSidebar = false"
                class="flex items-center justify-between px-5 py-2 transition-all duration-300" :class="[
                  $route.path.startsWith('/conversations')
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:bg-gray-50',
                ]">
                <div class="flex flex-col justify-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 flex-shrink-0" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    <span
                      class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                      AI-Assisted Messaging
                    </span>
                  </div>
                  <div class="flex ">
                    <a href="" class="text-[10px] text-gray-600 mr-2">
                      Module 1
                    </a>
                    <a href="" class="text-[10px] text-gray-600 ">
                      Automate chats smartly
                    </a>
                  </div>
                </div>
                <div class="ml-4 flex flex-col items-center">
                  <button @click="openHelpModal('/help/ai-messaging', 'Information and Video')"
                    class="cursor-pointer block">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                      fill="currentColor">
                      <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                      <polygon points="10,8 16,12 10,16" fill="currentColor" />
                    </svg>
                  </button>
                  <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
                </div>
              </router-link>
            </div>



            <div :class="[$route.path === '/calendar' || $route.path === '/analytics' || $route.path === '/content-library' ? 'bg-blue-50' : '']">
              <router-link to="/calendar" @click="showMobileSidebar = false"
                class="flex items-center justify-between px-5 py-3 transition-colors" :class="[
                  $route.path === '/calendar' || $route.path === '/analytics' || $route.path === '/content-library'
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:bg-gray-50',
                ]">
                <div class="flex flex-col justify-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span
                      class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                      Content Calendar
                    </span>
                  </div>
                  <div class="flex ">
                    <a href="" class="text-[10px] text-gray-600 mr-2">
                      Module 2
                    </a>
                    <a href="" class="text-[10px] text-gray-600 ">
                      Plan posts effectively
                    </a>
                  </div>
                </div>
                <div class="ml-4 flex flex-col items-center">
                  <button @click="openHelpModal('/help/content-calendar', 'Information and Video')"
                    class="cursor-pointer block">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                      fill="currentColor">
                      <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                      <polygon points="10,8 16,12 10,16" fill="currentColor" />
                    </svg>
                  </button>
                  <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
                </div>
              </router-link>
            </div>

            <div :class="[$route.path === '/monetization-hub' ? 'bg-blue-50' : '']">
              <router-link to="/monetization-hub" @click="showMobileSidebar = false"
                class="flex items-center justify-between px-5 py-2 transition-colors relative" :class="[
                  $route.path === '/monetization-hub'
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:bg-gray-50',
                ]">
                <div class="flex flex-col justify-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 10c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                    </svg>
                    <span
                      class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                      Subscription & PPV
                    </span>
                  </div>
                  <div class="flex ">
                    <a href="" class="text-[10px] text-gray-600 mr-2">
                      Module 3
                    </a>
                    <a href="" class="text-[10px] text-gray-600 ">
                      Monetize your content
                    </a>
                  </div>
                </div>
                <div class="ml-4 flex flex-col items-center">
                  <button @click="openHelpModal('/help/Subscription-ppv', 'Information and Video')"
                    class="cursor-pointer block">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                      fill="currentColor">
                      <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                      <polygon points="10,8 16,12 10,16" fill="currentColor" />
                    </svg>
                  </button>
                  <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
                </div>
              </router-link>
            </div>

            <div :class="[$route.path === '/analytics-dashboard' ? 'bg-blue-50' : '']">
              <router-link to="/analytics-dashboard" @click="showMobileSidebar = false"
                class="flex items-center justify-between px-5 py-2 transition-all duration-300 relative" :class="[
                  $route.path === '/analytics-dashboard'
                    ? 'text-blue-600'
                    : 'text-gray-600',
                ]">
                <!-- Left block: icon + label + subtitle -->
                <div class="flex flex-col justify-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 flex-shrink-0" fill="none" viewBox="0 0 24 24"
                      stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                    <span
                      class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                      Analytics Dashboard
                    </span>
                  </div>

                  <!-- Subtitle directly below title -->
                  <div class="flex ">
                    <a href="" class="text-[10px] text-gray-600 mr-2">
                      Module 4
                    </a>
                    <a href="" class="text-[10px] text-gray-600 ">
                      Visual performance metrics
                    </a>
                  </div>
                </div>

                <!-- Right big gray play icon -->
                <div class="ml-4 flex flex-col items-center">
                  <button @click="openHelpModal('/help/analytics-dashboard', 'Information and Video')"
                    class="cursor-pointer block">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                      fill="currentColor">
                      <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                      <polygon points="10,8 16,12 10,16" fill="currentColor" />
                    </svg>
                  </button>
                  <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
                </div>


              </router-link>
            </div>

            <div :class="[
              $route.path === '/revenue-optimization' ? 'bg-blue-50' : '',
            ]">
              <router-link to="/revenue-optimization" @click="showMobileSidebar = false"
                class="flex items-center justify-between px-5 py-2 transition-colors" :class="[
                  $route.path === '/revenue-optimization'
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:bg-gray-50',
                ]">
                <div class="flex flex-col justify-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span
                      class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                      Advanced Revenue
                    </span>
                  </div>
                  <div class="flex ">
                    <a href="" class="text-[10px] text-gray-600 mr-2">
                      Module 5
                    </a>
                    <a href="" class="text-[10px] text-gray-600 ">
                      Optimize earnings smartly

                    </a>
                  </div>
                </div>
                <div class="ml-4 flex flex-col items-center">
                  <button @click="openHelpModal('/help/advance-revenue-optimization', 'Information and Video')"
                    class="cursor-pointer block">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                      fill="currentColor">
                      <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                      <polygon points="10,8 16,12 10,16" fill="currentColor" />
                    </svg>
                  </button>
                  <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
                </div>
              </router-link>
            </div>

            <div :class="[
              $route.path === '/smart-content-tagging' ? 'bg-blue-50' : '',
            ]">
              <router-link to="/smart-content-tagging" @click="showMobileSidebar = false"
                class="flex items-center justify-between px-5 py-2 transition-colors" :class="[
                  $route.path === '/smart-content-tagging'
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:bg-gray-50',
                ]">
                <!-- Left: Icon and Title -->
                <div class="flex flex-col justify-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                      Smart Content Tagging
                    </span>
                  </div>

                  <div class="flex ">
                    <a href="" class="text-[10px] text-gray-600 mr-2">
                      Module 6
                    </a>
                    <a href="" class="text-[10px] text-gray-600 ">
                      Tag content with AI
                    </a>
                  </div>
                </div>

                <!-- Right video icon -->
                <div class="ml-4 flex flex-col items-center">
                  <button @click="openHelpModal('/help/smart-content', 'Information and Video')"
                    class="cursor-pointer block">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                      fill="currentColor">
                      <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                      <polygon points="10,8 16,12 10,16" fill="currentColor" />
                    </svg>
                  </button>
                  <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
                </div>
              </router-link>
            </div>

            <div :class="[$route.path === '/security-privacy' ? 'bg-blue-50' : '']">
              <router-link to="/security-privacy" @click="showMobileSidebar = false"
                class="flex items-center justify-between px-5 py-2 transition-colors" :class="[
                  $route.path === '/security-privacy'
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:bg-gray-50',
                ]">
                <!-- Left Content -->
                <div class="flex flex-col justify-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-600" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M12 22s8-4 8-10V5a2 2 0 00-2-2H6a2 2 0 00-2 2v7c0 6 8 10 8 10z" />
                    <path d="M9 12l2 2 4-4" />
                  </svg>
                    <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                      Security & Privacy
                    </span>
                  </div>

                  <div class="flex ">
                    <a href="" class="text-[10px] text-gray-600 mr-2">
                      Module 7
                    </a>
                    <a href=" " class="text-[11px] text-gray-600 ">
                      Data safety tools
                    </a>
                  </div>
                </div>

                <!-- Right video icon -->
                <div class="ml-4 flex flex-col items-center">
                  <button @click="openHelpModal('/help/security-privacy', 'Information and Video')"
                    class="cursor-pointer block">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                      fill="currentColor">
                      <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                      <polygon points="10,8 16,12 10,16" fill="currentColor" />
                    </svg>
                  </button>
                  <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
                </div>
              </router-link>
            </div>

            <div :class="[$route.path === '/Onboarding&Migration' ? 'bg-blue-50' : '']">
              <router-link to="/Onboarding&Migration" @click="showMobileSidebar = false"
                class="flex items-center justify-between px-5 py-2 transition-colors" :class="[
                  $route.path === '/Onboarding&Migration'
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:bg-gray-50',
                ]">
                <div class="flex flex-col justify-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 " fill="none" viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                      Onboarding & Migration
                    </span>
                  </div>
                  <div class="flex ">
                    <a href="" class="text-[10px] text-gray-600 mr-2">
                      Module 8
                    </a>
                    <a href=" " class="text-[11px] text-gray-600 ">
                      Trusted Migration Tools
                    </a>
                  </div>
                </div>
                <div class="ml-4 flex flex-col items-center">
                  <button @click="openHelpModal('/help/Onboarding-Migration', 'Information and Video')"
                    class="cursor-pointer block">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                      fill="currentColor">
                    <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                    <polygon points="10,8 16,12 10,16" fill="currentColor" />
                  </svg>
                  </button>
                  <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
                </div>
              </router-link>
            </div>
            <div :class="[$route.path.startsWith('/connect-accounts') ? 'bg-blue-50' : '']">
              <router-link to="/connect-accounts/account" @click="showMobileSidebar = false"
                class="flex items-center justify-between px-5 py-2 transition-colors relative" :class="[
                  $route.path.startsWith('/connect-accounts')
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:bg-gray-50',
                ]">
                <div class="flex flex-col justify-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
                    <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">Connect Accounts</span>
                  </div>
                  <div class="flex ">
                    <a href="" class="text-[10px] text-gray-600 mr-2">
                      Module 9
                    </a>
                    <a href=" " class="text-[11px] text-gray-600 ">
                      Links your social account
                    </a>
                  </div>
                </div>
                <div class="ml-4 flex flex-col items-center">
                  <button @click="openHelpModal('/help/connect-accounts', 'Information and Video')"
                    class="cursor-pointer block">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                      fill="currentColor">
                      <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                      <polygon points="10,8 16,12 10,16" fill="currentColor" />
                    </svg>
                  </button>
                  <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
                </div>
              </router-link>
            </div>

            <!-- Fan Database (icon, text, and subtitle for alignment, mobile) -->
            <div :class="[$route.path === '/fan-insights' ? 'bg-blue-50' : '']">
              <router-link to="/fan-insights" @click="showMobileSidebar = false"
                class="flex items-center justify-between px-5 py-2 transition-colors relative" :class="[
                  $route.path === '/fan-insights'
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:bg-gray-50',
                ]">
                <div class="flex flex-col justify-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">Fan Database</span>
                  </div>
                  <div class="flex ">
                    <a href="" class="text-[10px] text-gray-600 mr-2">
                      Module 10
                    </a>
                    <a href=" " class="text-[11px] text-gray-600 ">
                      View and manage your fans
                    </a>
                  </div>
                </div>
                <div class="ml-4 flex flex-col items-center">
                  <button @click="openHelpModal('/help/fan-database', 'Information and Video')"
                    class="cursor-pointer block">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                      fill="currentColor">
                      <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                      <polygon points="10,8 16,12 10,16" fill="currentColor" />
                    </svg>
                  </button>
                  <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
                </div>
              </router-link>
            </div>

            <!-- Mobile Experience (mobile) -->
            <div>
              <button @click="openHelpModal('/help/mobile-experience', 'Information and Video')"
                class="flex items-center justify-between w-full px-5 py-2 transition-colors relative text-gray-600 hover:bg-gray-50">
                <div class="flex flex-col justify-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">Mobile Experience</span>
                  </div>

                  <!-- Subtitle directly below title -->
                  <div class="ml-10 -mt-1">
                    <a href="" class="text-[10px] text-gray-600 ">
                      Optimize for mobile devices
                    </a>
                  </div>
                </div>
                <div class="ml-4 flex flex-col items-center">
                  <button @click.stop="openHelpModal('/help/mobile-experience', 'Information and Video')"
                    class="cursor-pointer block">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                      fill="currentColor">
                      <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                      <polygon points="10,8 16,12 10,16" fill="currentColor" />
                    </svg>
                  </button>
                  <span class="text-[10px] text-gray-500 mt-1">Info & Video</span>
                </div>
              </button>
            </div>

            <!-- OnlyFans Connect Button (Mobile) -->
           <!--  <div class="mt-4 border-t border-gray-200 pt-4">
              <button @click="openOnlyFansModal"
                class="flex items-center w-full px-5 py-3 transition-colors text-gray-600 hover:bg-blue-50 hover:text-blue-600">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                  Connect OnlyFans
                </span>
              </button>
            </div> -->

            <!-- Admin Section (Mobile Only for Admins) -->
            <div v-if="isAdmin" class="mt-2 text-blue-600">
              <button @click="isAdminOpen = !isAdminOpen"
                class="flex items-center justify-between w-full px-5 py-3 text-gray-600 hover:bg-gray-50 transition-colors">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0 font-medium">Admin</span>
                </div>
                <svg :class="{'rotate-180': isAdminOpen}" class="h-4 w-4 transform transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              <div v-show="isAdminOpen" class="bg-gray-800">
                <router-link to="/admin/support" @click="showMobileSidebar = false"
                  class="flex items-center pl-14 py-2 text-sm transition-colors text-gray-300 hover:text-white hover:bg-gray-700">
                  Support
                </router-link>
              </div>
            </div>

            <!-- Logout Tab (Mobile) -->
            <div class="mt-2 border-t border-gray-200 pt-4">
              <button @click="handleLogout"
                class="flex items-center w-full px-5 py-3 transition-colors text-gray-600 hover:bg-red-50 hover:text-red-600">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">
                  Logout
                </span>
              </button>
            </div>
            <!-- Authentic Voice AI -->
            <!-- <router-link to="/authentic-voice-ai" @click="showMobileSidebar = false"
              class="flex items-center px-5 py-3 transition-colors" :class="[
                $route.path === '/authentic-voice-ai'
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50',
              ]">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 1a3 3 0 00-3 3v7a3 3 0 006 0V4a3 3 0 00-3-3zm6 10a6 6 0 01-12 0m12 0v2a6 6 0 01-6 6m0 0v3m0-3a6 6 0 01-6-6v-2" />
            </svg>
              <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap opacity-100 translate-x-0">Authentic Voice AI</span>
            </router-link> -->
            <!-- Cross-Platform Control Center (Mobile) -->
            <!-- <div :class="[$route.path === '/cross-platform-control' ? 'bg-blue-50' : '']"> -->
            <!-- <router-link to="/cross-platform-control" @click="showMobileSidebar = false"
              class="flex items-center justify-between px-5 py-2 transition-all duration-300 relative" :class="[
                $route.path === '/cross-platform-control'
                  ? 'text-blue-600'
                  : 'text-gray-600',
              ]">
              <div class="flex flex-col justify-center">
                <div class="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 flex-shrink-0" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 0a2 2 0 012 2v6a2 2 0 01-2 2m-6 0a2 2 0 002-2v-6a2 2 0 00-2-2" />
                  </svg>
                  <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap" :class="[
                    isExpanded || isMobile
                      ? 'opacity-100 translate-x-0'
                      : 'opacity-0 -translate-x-4',
                  ]">
                    Cross-Platform Control
                  </span>
                </div> -->

            <!-- Subtitle below title -->
            <!-- <div v-if="isExpanded || isMobile" class="ml-10 -mt-1">
                  <a href="" class="text-[10px] text-gray-600">
                    Manage all platforms
                  </a>
                </div> -->
            <!-- </div> -->

            <!-- Right big gray play icon -->
            <!-- <button @click="openHelpModal('/help/cross-platform-control', 'Cross-Platform Control Center Help')"
                class="ml-4 cursor-pointer block">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                  fill="currentColor">
                  <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                  <polygon points="10,8 16,12 10,16" fill="currentColor" />
                </svg>
              </button> -->

            <!-- </router-link> -->
            <!-- </div> -->

            <!-- Collaborative Content Tools -->
            <!-- <div :class="[
              $route.path === '/collaborative-content-tools' ? 'bg-blue-50' : '',
            ]"> -->
            <!-- <router-link to="/collaborative-content-tools" @click="showMobileSidebar = false"
                class="flex items-center justify-between px-5 py-2 transition-all duration-300 relative" :class="[
                  $route.path === '/collaborative-content-tools'
                    ? 'text-blue-600'
                    : 'text-gray-600',
                ]">
                <div class="flex flex-col justify-center">
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 flex-shrink-0" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 515.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span class="ml-4 text-base transition-all duration-300 whitespace-nowrap" :class="[
                      isExpanded || isMobile
                        ? 'opacity-100 translate-x-0'
                        : 'opacity-0 -translate-x-4',
                    ]">
                      Collaborative Content Tools
                    </span>
                  </div> -->

            <!-- Subtitle below title -->
            <!-- <div v-if="isExpanded || isMobile" class="ml-10 -mt-1">
                    <a href="" class="text-[10px] text-gray-600">
                      Team content workflows
                    </a>
                  </div> -->
            <!-- </div> -->

            <!-- Right big gray play icon -->
            <!-- <button @click="openHelpModal('/help/collaborative-content', 'Collaborative Content Tools Help')"
                  class="ml-4 cursor-pointer block">
                  <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-600" viewBox="0 0 24 24"
                    fill="currentColor">
                    <circle cx="12" cy="12" r="10" fill="#DBEAFE" />
                    <polygon points="10,8 16,12 10,16" fill="currentColor" />
                  </svg>
                </button> -->

            <!-- </router-link> -->
            <!-- </div> -->
          </nav>

          <!-- User Profile Section (same as desktop) -->
          <!-- <div class="p-5 mt-auto"> -->
          <!-- <div class="w-full flex mb-3">
              <button @click="goToSubscription" :class="[
                'flex w-full border rounded-xl bg-white shadow-sm transition-colors  font-medium group items-center justify-center',
                $route.path === '/subscription'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-gray-200 text-[#222]',
                'focus:border-blue-500 focus:text-blue-600 hover:border-blue-500 hover:text-blue-600 active:border-blue-600 active:text-blue-700',
                'flex-col',
              ]">
                <div class="flex flex-col w-full">
                  <div class="flex items-center justify-between w-full px-2 py-1">
                    <div class="flex items-center gap-2">
                      <span class="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-blue-500" fill="none"
                          viewBox="0 0 24 24" stroke="currentColor">
                          <circle cx="12" cy="12" r="10" fill="#e0edff" />
                          <path stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                            d="M12 16V8m0 0l-4 4m4-4l4 4" />
                        </svg>
                      </span>
                      <span :class="[
                        'text-base font-medium',
                        $route.path === '/subscription'
                          ? 'text-blue-600'
                          : 'text-[#222] group-hover:text-blue-600 group-focus:text-blue-600 group-active:text-blue-700',
                      ]">
                        {{
                        profileData?.subscription?.planId?.name === "Elite"
                        ? "Elite"
                        : "Upgrade Plan"
                        }}
                      </span>
                    </div>
                    <span class="text-sm text-gray-500">{{
                      profileData?.subscription?.planId?.name || "No Plan"
                      }}</span>
                  </div>
                  <p class="text-xs text-gray-500 px-2 pb-2 text-center">
                    Upgrade to unlock AI tools and insights.
                  </p>
                </div>
                <span v-else class="flex items-center justify-center h-8 w-8 rounded-full bg-blue-100 m-auto">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor">
                    <circle cx="12" cy="12" r="10" fill="#e0edff" />
                    <path stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                      d="M12 16V8m0 0l-4 4m4-4l4 4" />
                  </svg>
                </span>
              </button>
            </div> -->
          <!-- <div class="flex flex-row items-center justify-between w-full">
              <div class="flex items-center cursor-pointer" @click="goToProfile">
                <img :src="profileData?._doc.profileImgUrl &&
                  profileData._doc.profileImgUrl !== ''
                  ? `${apiUrl}${profileData._doc.profileImgUrl}`
                  : 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAOEAAADhCAMAAAAJbSJIAAAAV1BMVEX6+vqPj4////+Li4u5ubn8/PyIiIiFhYWJiYnk5OShoaGnp6fT09Pn5+eRkZHu7u7Z2dn19fXCwsKamprHx8exsbHOzs7X19eurq6/v7+jo6Pe3t6WlpZaNtXmAAAE3UlEQVR4nO2d25aqOhBFsUIRbgqI4AX//zsP0fa0vUfbBoKm4ljzpfvROapIIGSFKAIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAIEWamG+P/vn/Owhi5Juu3XZHnp6Lblutm1PT9q5aDKRriVulEqZVBqUSr9pjxh0gyrWOlr273KL05Vh/gyDTkv+jdJIsscEemrNUP9K7oU0W+f6UD1Bz+9rs4xuEOrFSrR/15T7rJwiwjU/y8gF9l3IWoyHxKLAVHxS68AYej1qZDbyRFaIocbaYIjhNHHlajTqygIS2CUqRiquDYqHFAinS0H2S+0WUwijzYThP/KFahjDY8vUWvtIEUkeK5hkkYMz9X83rUoJsQ+pTy2YIrFcJ4ytn8EoZRRCocBEMoostVeFH0LfAUOs4dSK8kpfQ2pbOT4Gp1Et6mvHZr0vEOXPhYQ7vU0TCphRueHAXFj6bsKij95pSrOY9N/xQxktymPLgbJqKfobh3HWhGw0GyIW3d5vuLoeg5f/6j4TdpL9qwczdUoh+DYWhDuhPdpY5PFhdD2dfhboGxdC/ZkMsFZvxMtOH64+9pGnfDjWTBBR7xxT/ku08XqejpcGzTvWub6rXsLnW/EIVfhu7LGNIXMdxnRC16NjRw5FZD2as0F9xuTWU//l7hxmVNeCO/hKaI89dqdAAljBxe4wdxFRp4P7dPpc/2/zNnv5AhFT8X3uBonuE5FMG57/IT4e/VfkDldEU9hFPCyCx+T1XU+6AEzaw4TVH3gQmaZbcpisFV0DDlWkzD3K1Pa8ud0EnbBClotut3NmXUx9B2sd9B2fmZo86DjgVFTOXmr4d+fa4DLuAV4rJ9EF5TOg/fz2ACiBud/rRUiT5vPyF+eIWJ1v3hnGidGMY/566sPione00CR1U21HU9rCs2YWffP+kV8A3fPwQAAIAP7k/1WApJkwpTM/THeFmOfRYJuelhGgo13nYuTaJX3VqCI1W5awDhIUof/K+hzlkZneKY+F7Bmb4uOhXPq3DUv1rQ85t916CaHcrjtegSF51gePDWp1y/o4Q+X5y+p4RjETtPRVxiq6UlnmrovkvPFl9tusS2dTt87SNaInpgh68IBh3eJLhSWxjCcK7h265DX4afP9IsEDa0w1cUaomQkx2+olBLhJwsDT09IrqfEGFt6CkKxY17cNsOb3ujqX2Tobfj+N41mCbeUqVzT56bis+T6t4i6HN/+3va1Gde7z3zhdfd0e4H7jzHb5rN7fg5OzwfUjc3WmGPOvp9NeOW47Iy9P16jXavvf3W/o/+ovyVfeptufsO19Do34IiwmxLnO/1EP8vuQ30sttTJeWIjFcpihE0W/Jf0KhqI0fQbDmZeIz+c9JWxjV4g7lYtlN1LGGz0A+of/jBnOkoJTGMSM1iZdSdzNMhmYbzEiOObkVsZ/sVpv7PDJCdn+wcDfH+UQbIhiByQkzZQc8qpEqSWG5/3sMUlYVOJn5nRieHOpxPzfEoWXcbW0uT8oqHcPS+GH9wVXZ33wT81c18JzCP96F+DfGS5lrvt4d8oy65tTS9bJZOr/k1dc67XV1Foae8Lrv4uamqoS77frfd7nZ9X9ZZ1TQsbEe+E1+Zte+gARJsAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACJP/AAFSQ7wNy+LTAAAAAElFTkSuQmCC'
                  " class="h-10 w-10 rounded-full object-cover" alt="User Profile" />
                <div class="ml-4">
                  <p class="text-sm font-medium text-gray-900">
                    {{ profileData?._doc.firstName || "" }}
                  </p>
                  <p class="text-xs text-gray-500">Creator</p>
                </div>
              </div>
              <button @click="handleLogout"
                class="flex items-center text-gray-500 hover:text-red-500 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div> -->
          <!-- </div> -->
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import axiosInstance from "../utils/axios";
import GlobalChat from "./GlobalChat.vue";
import { useToast } from 'vue-toastification/dist/index.mjs';

export default {
  name: "NavSidebar",
  components: {
    GlobalChat
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      isCalendarOpen: false,
      isMobile: false,
      user: null,
      profileData: null,
      apiUrl: process.env.VUE_APP_API_URL || "http://localhost:5000",
      showMobileSidebar: false,
      showHelpModal: false,
      helpModalUrl: '',
      helpModalTitle: '',
      userName: null,
      showOnlyFansModal: false,
      onlyFansForm: {
        email: '',
        password: '',
      },
      isConnecting: false,
      isAdminOpen: false,
    };
  },
  mounted() {
    this.checkScreenSize();
    window.addEventListener("resize", this.checkScreenSize);
    this.fetchUserProfile();
    this.updateSidebarWidth();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.checkScreenSize);
  },
  methods: {
    async fetchUserProfile() {
      try {
        const response = await axiosInstance.get(`/api/profile/`, {});
        this.profileData = response.data.data;
        this.userName = response.data.data.firstName;
      } catch (error) {
        console.error("Error fetching profile data:", error);
      }
    },
    checkScreenSize() {
      this.isMobile = window.innerWidth < 1024; // lg breakpoint
    },

    toggleCalendar() {
      this.isCalendarOpen = !this.isCalendarOpen;
      this.showMobileSidebar = false;
      this.$emit("calendar");
    },
    closeCalendarSidebar() {
      this.isCalendarOpen = false;
    },
    handleLogout() {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("creator");
      localStorage.removeItem("data");
      this.$router.push("/");
    },
    goToProfile() {
      this.$router.push("/profile");
      this.showMobileSidebar = false;
    },
    goToSubscription() {
      this.$router.push("/subscription");
      this.showMobileSidebar = false;
    },
    goToWelcome() {
      this.$router.push("/welcome");
      this.showMobileSidebar = false;
    },
    updateSidebarWidth() {
      // Get actual sidebar width and set as CSS variable
      this.$nextTick(() => {
        const sidebar = document.querySelector('.sidebar-desktop');
        if (sidebar) {
          const sidebarWidth = sidebar.offsetWidth;
          document.documentElement.style.setProperty('--sidebar-width', `${sidebarWidth}px`);
        }
      });
    },
    openHelpModal(url, title) {
      this.helpModalUrl = url;
      this.helpModalTitle = title;
      this.showHelpModal = true;
      this.showMobileSidebar = false;
      // Update sidebar width when opening modal
      this.updateSidebarWidth();
    },
    closeHelpModal() {
      this.showHelpModal = false;
      this.helpModalUrl = '';
      this.helpModalTitle = '';
    },
    openOnlyFansModal() {
      this.showOnlyFansModal = true;
      this.showMobileSidebar = false;
    },
    async connectOnlyFans() {
      this.isConnecting = true;
      try {
        const response = await axiosInstance.post('/api/onlyfans/connect', this.onlyFansForm);
        console.log('OnlyFans connection successful:', response.data);
        // Show success toast
        this.toast.success(response.data.message);
        this.closeOnlyFansModal();
      } catch (error) {
        console.error('Error connecting to OnlyFans:', error);
        // Show error toast
        this.toast.error('Failed to connect OnlyFans account. Please try again.');
      } finally {
        this.isConnecting = false;
      }
    },
    closeOnlyFansModal() {
      this.showOnlyFansModal = false;
      this.onlyFansForm = {
        email: '',
        password: '',
      };
    },
  },
  computed: {
    capitalizedName() {
      const name = this.userName || (this.profileData?._doc?.firstName ?? "");
      if (!name) return "";
      return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    },
    isAdmin() {
      // Check from profileData first, handle possible nesting in _doc
      const role = this.profileData?.role || this.profileData?._doc?.role;
      if (role === 'admin') return true;
      
      const creatorStr = localStorage.getItem('creator');
      if (creatorStr) {
        try {
          const creator = JSON.parse(creatorStr);
          const localRole = creator.role || creator._doc?.role;
          return localRole === 'admin';
        } catch (e) {
          return false;
        }
      }
      return false;
    },
    resolvedHelpComponent() {
      if (!this.helpModalUrl) return null;
      
      const mapping = {
        '/help/analytics-dashboard': defineAsyncComponent(() => import('@/views/Module1.vue')),
        '/help/ai-messaging': defineAsyncComponent(() => import('@/views/AIMessaging.vue')),
        '/help/content-calendar': defineAsyncComponent(() => import('@/views/ContentCalendar.vue')),
        '/help/Subscription-ppv': defineAsyncComponent(() => import('@/views/SubscriptionPPV.vue')),
        '/help/advance-revenue-optimization': defineAsyncComponent(() => import('@/views/AdvanceRevenueOptimization.vue')),
        '/help/smart-content': defineAsyncComponent(() => import('@/views/SmartContent.vue')),
        '/help/security-privacy': defineAsyncComponent(() => import('@/views/securityPrivacy.vue')),
        '/help/Onboarding-Migration': defineAsyncComponent(() => import('@/views/OnboardingAndMigration.vue')),
        '/help/connect-accounts': defineAsyncComponent(() => import('@/views/ConnectAccounts.vue')),
        '/help/fan-database': defineAsyncComponent(() => import('@/views/FanDatabase.vue')),
        '/help/mobile-experience': defineAsyncComponent(() => import('@/views/MobileExperienceHelp.vue')),
        '/help/Api-Intrigation': defineAsyncComponent(() => import('@/views/APIIntegration.vue')),
      };

      const key = Object.keys(mapping).find(k => this.helpModalUrl.startsWith(k));
      return key ? mapping[key] : null;
    }
  }
};
</script>

<style scoped>
.router-link-active {
  font-weight: 600;
}

@media (max-width: 1023px) {
  .w-72 {
    transition: width 0.3s ease;
  }

  .w-20 {
    transition: width 0.3s ease;
  }
}

/* Add smooth transitions for all text elements */
span {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

/* Ensure icons stay fixed during transition */
svg {
  flex-shrink: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Hide scrollbars but allow scrolling */
.custom-scrollbar {
  scrollbar-width: none;
  /* Firefox */
  -ms-overflow-style: none;
  /* IE 10+ */
  overflow-y: auto;
  overflow-x: hidden;
}

.custom-scrollbar::-webkit-scrollbar {
  display: none;
  /* Chrome, Safari, Opera */
}

.custom-greeting {
  margin-top: 12px;
  text-align: center;
  font-size: 22px;
}

/* Ensure sidebar always stays visible above modals */
.sidebar-desktop {
  z-index: 60 !important;
  position: relative !important;
  background-color: white;
  isolation: isolate; /* Create new stacking context to keep sidebar above everything */
}
</style>

<style>
/* Help Modal Styles - Positioned in main content area, not covering sidebar */
.help-modal-container {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  /* Start from sidebar edge */
  left: var(--sidebar-width, 360px);
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  overflow: hidden;
}

.help-modal-overlay {
  position: absolute !important;
  inset: 0 !important;
  background-color: rgba(0, 0, 0, 0.5) !important;
  pointer-events: auto;
  z-index: 45 !important;
}

.help-modal-content {
  position: relative;
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  width: 90%;
  max-width: 1200px;
  max-height: 90vh;
  height: auto;
  overflow: hidden;
  pointer-events: auto;
  z-index: 50;
  padding: 0 1rem;
  box-sizing: border-box;
}

/* Mobile and Tablet responsive - modal covers full screen */
@media (max-width: 1023px) {
  .help-modal-container {
    left: 0 !important;
  }

  .help-modal-content {
    width: 90vw !important;
    max-width: 90vw !important;
  }
}

/* Small mobile devices */
@media (max-width: 640px) {
  .help-modal-content {
    width: calc(100vw - 1rem) !important;
    max-width: calc(100vw - 1rem) !important;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}

/* Help Content Wrapper overrides for screenshots */
.help-content-wrapper > * {
  min-height: 0 !important;
  background-color: white !important;
}
</style>
